import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import App from './App';
import { setI18n, setSelectedLanguage } from './Store/features/i18n/i18nSlice';
import I18nService from './components/Utility/I18nService';
import {
    setAllThemes,
    setCurrentThemeColor,
    setSelectedTheme,
    setSelectedThemeSwitcherState,
} from './Store/features/themes/themesSlice';
import { setStartTour } from './Store/features/joyride/joyrideSlice';
import {
    setUserAccessToken,
    setUserExpiresAt,
    setUserIsLoggedIn,
    setUserkey,
    setUserSettings,
} from './Store/features/userSettings/userSettingsSlice';
import { setApiBaseUrl, setSettings } from './Store/features/settings/settingsSlice';
import requestClient from './components/Utility/RequestClient';
import {
    setShowUserStatistics,
    setDemoInstance,
    setReloadPrompts,
} from './Store/features/environment/environmentSlice';
import { Button } from './components/Utility/Button';
import CryptoJS from 'crypto-js';
import usePromptState from './Hooks/state/usePromptState';
import { RootState, useAppDispatch, useAppSelector } from './store';

function InitSettings() {
    const apiBaseUrl = 'https://' + document.location.hostname + '/api';
    const settings = useAppSelector((state: RootState) => state.settings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const [loadingStates, setLoadingStates] = useState({
        translations: false,
        userSettings: false,
        generalSettings: false,
        prompts: false,
        userPrompts: false,
        themes: false,
        cssLocal: false,
        cssTheme: false,
        password: false,
    });
    const [userDataLoaded, setUserDataLoaded] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [passwordSet, setPasswordSet] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [password, setPassword] = useState('');
    const [isResetting, setIsResetting] = useState(false);
    const [language, setLanguage] = useState<string>('de');
    const requestClientRef = useRef(requestClient);
    const projectDomain = document.getElementById('root')?.dataset.projectDomain;
    const selectedTheme = useAppSelector((state: RootState) => (state as any).themes?.selectedTheme || '');
    const demoInstance = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    const themeSwitcherState = useAppSelector((state: RootState) => (state as any).themes?.themeSwitcherState || '');
    const themes = useAppSelector((state: RootState) => (state as any).themes?.allThemes || {});
    const reloadSettings = useAppSelector((state: RootState) => state.settings.reloadSettings);
    const reloadPrompts = useAppSelector((state: RootState) => state.environment.reloadPrompts);

    const { setPrompts } = usePromptState();

    useEffect(() => {
        requestClientRef.current = requestClient;
    }, []);

    const dispatch = useAppDispatch();
    const selectedLanguage = useAppSelector((state: RootState) => state.i18n.selectedLanguage);
    const userSettings = useAppSelector((state: RootState) => state.userSettings);

    useEffect(() => {
        if (navigator.language.startsWith('en')) {
            setLanguage('en');
        } else if (navigator.language.startsWith('fr')) {
            setLanguage('fr');
        } else {
            setLanguage('de');
        }
    }, []);

    useEffect(() => {
        if (userSettings.accessToken && settings.apiBaseUrl && userDataLoaded) {
            if (selectedLanguage === '') {
                const data = new URLSearchParams();
                data.append('language', language);

                requestClient.post(`/settings/updateusersettings`, data).then((response: any) => {
                    dispatch(setSelectedLanguage(language));
                });
            }
        }
    }, [dispatch, userSettings.accessToken, settings.apiBaseUrl, userDataLoaded, selectedLanguage, language]);

    useEffect(() => {
        requestClientRef.current = requestClient;
    }, [userSettings.accessToken, selectedLanguage]);

    useEffect(() => {
        const fetchSettings = async () => {
            if (apiBaseUrl && settings.systemStatus === 'running') {
                dispatch(setApiBaseUrl(apiBaseUrl));
            }
        };

        fetchSettings();
    }, [apiBaseUrl, dispatch, settings.systemStatus]);

    // fetch general settings
    useEffect(() => {
        let counter = 0;
        const fetchSettings = async () => {
            if (settings.apiBaseUrl && settings.systemStatus === 'running' && userSettings.isLoggedIn) {
                await axios
                    .get(`${settings.apiBaseUrl}/settings/get`, {
                        headers: {
                            UserLanguage: selectedLanguage,
                            Provider: localStorage.getItem('loginProvider') ?? 'oidc',
                        },
                    })
                    .then(async (response: any) => {
                        const settingsData = {
                            ...response.data,
                            hybridSearchVectorWeight:
                                response.data.hybridSearchVectorWeight !== undefined &&
                                response.data.hybridSearchVectorWeight !== null &&
                                response.data.hybridSearchVectorWeight !== ''
                                    ? Number(response.data.hybridSearchVectorWeight)
                                    : 0.5,
                            hybridSearchTextWeight:
                                response.data.hybridSearchTextWeight !== undefined &&
                                response.data.hybridSearchTextWeight !== null &&
                                response.data.hybridSearchTextWeight !== ''
                                    ? Number(response.data.hybridSearchTextWeight)
                                    : 0.5,
                            chunkSize: response.data.chunkSize ?? 2000,
                            chunkOverlap: response.data.chunkOverlap ?? 500,
                        };
                        dispatch(setSettings(settingsData));
                        dispatch(setShowUserStatistics(response.data.userStatistic));
                        dispatch(setDemoInstance(response.data.demo));
                        setLoadingStates((prev) => ({ ...prev, generalSettings: true }));
                    })
                    .catch(() => {
                        if (counter === 0) {
                            counter++;
                            setTimeout(fetchSettings, 10000);
                        }
                    });
            }
        };

        fetchSettings();
    }, [
        settings.apiBaseUrl,
        selectedLanguage,
        dispatch,
        settings.systemStatus,
        userSettings.isLoggedIn,
        reloadSettings,
    ]);

    // get access token or redirect to login
    useEffect(() => {
        if (!userSettings.accessToken && settings.apiBaseUrl) {
            const getToken = function () {
                if (
                    document.location.search.includes('accountIdentifier=') &&
                    document.location.search.includes('authenticationProviderName=') &&
                    document.location.search.includes('token=')
                ) {
                    const expiresAt = document.cookie.replace(/(?:(?:^|.*;\s*)expiresAt\s*=\s*([^;]*).*$)|^.*$/, '$1');

                    if (expiresAt) {
                        dispatch(setUserAccessToken(expiresAt));
                        dispatch(setUserExpiresAt(parseInt(expiresAt)));
                        dispatch(setUserIsLoggedIn(true));
                    } else {
                        dispatch(setUserAccessToken(''));
                        dispatch(setUserIsLoggedIn(false));
                        document.location.reload();
                    }
                } else if (document.location.pathname.startsWith('/providerlogin/')) {
                    const provider = document.location.pathname.replace('/providerlogin/', '');
                    localStorage.setItem('loginProvider', provider);
                    document.location.href = `${settings.apiBaseUrl}/providerlogin/${provider}?redirectUri=${document.location.href}`;
                } else {
                    localStorage.setItem('loginProvider', 'oidc');
                    document.location.href = `${settings.apiBaseUrl}/login?redirectUri=` + document.location.href;
                }
            };
            getToken();
        }
    }, [settings.apiBaseUrl, userSettings.accessToken, dispatch, settings.registrationEnabled]);

    // read token and expiresAt from cookie and set in userSettings
    useEffect(() => {
        if (!userSettings.accessToken) {
            // const token = document.cookie.replace(/(?:(?:^|.*;\s*)token\s*=\s*([^;]*).*$)|^.*$/, '$1');
            const expiresAt = document.cookie.replace(/(?:(?:^|.*;\s*)expiresAt\s*=\s*([^;]*).*$)|^.*$/, '$1');

            if (expiresAt) {
                dispatch(setUserAccessToken(expiresAt));
                dispatch(setUserExpiresAt(parseInt(expiresAt)));
                dispatch(setUserIsLoggedIn(true));
            }
        }
    }, [userSettings.accessToken, dispatch]);

    // fetch user settings
    useEffect(() => {
        if (userSettings.accessToken && settings.apiBaseUrl) {
            const fetchUserSettings = async () => {
                await requestClientRef.current.get(`/settings/getusersettings`).then(async (response: any) => {
                    dispatch(setUserSettings(response.data));
                    dispatch(setSelectedLanguage(response.data.language));

                    if (demoInstance) {
                        //  dispatch(setDemoInstance(!(response.data.isAdmin || response.data.isSuperAdmin)));
                    }

                    setUserDataLoaded((prev) => true);
                    setLoadingStates((prev) => ({ ...prev, userSettings: true }));

                    if (settings.registrationEnabled && !response.data.roles.includes('Avency.Jwt:ActiveUser')) {
                        setTimeout(fetchUserSettings, 5000);
                    }
                });
            };

            fetchUserSettings();
        }
    }, [
        settings.apiBaseUrl,
        userSettings.accessToken,
        settings.registrationEnabled,
        setUserDataLoaded,
        dispatch,
        demoInstance,
    ]);

    const hash: (content: string) => Promise<string> = async (content: string) => {
        if (window.crypto && window.crypto.subtle) {
            const encoder = new TextEncoder();
            const data2 = encoder.encode(content);
            const hash2 = await crypto.subtle.digest('SHA-256', data2);
            return Array.from(new Uint8Array(hash2))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('');
        }
        const hash = CryptoJS.SHA256(content);
        return hash.toString(CryptoJS.enc.Hex);
    };

    useEffect(() => {
        const getUserKey = async () => {
            let userPasswordAddition = '';
            if (userSettings.hasUserPassword) {
                const userPassword = localStorage.getItem('userPassword') ?? '';
                if ((await hash(userPassword)) !== userSettings.userPasswordHash) {
                    setShowPasswordModal((prev) => true);
                    return;
                }

                userPasswordAddition = userPassword;
            }

            const userkey = await hash(userPasswordAddition + userSettings.identifier + 'kj25n4br3vqbewnkj');

            dispatch(setUserkey(userkey));
            document.cookie = `userkey=${userkey}; path=/; secure; samesite=strict`;

            setLoaded((prev) => true);
            setPasswordSet((prev) => true);
            setLoadingStates((prev) => ({ ...prev, password: true }));
        };

        if (i18n !== null) {
            getUserKey();
        }
    }, [dispatch, userSettings.hasUserPassword, userSettings.userPasswordHash, userSettings.identifier, i18n]);

    // fetch translations
    useEffect(() => {
        if (userSettings.isLoggedIn && settings.apiBaseUrl !== '' && userDataLoaded && selectedLanguage !== '') {
            const fetchTranslations = () => {
                fetch(settings.apiBaseUrl + '/settings/xliff?locale=' + selectedLanguage, {
                    cache: 'no-cache',
                })
                    .then((response) => {
                        response.json().then((jsonData) => {
                            const i18n = new I18nService();
                            i18n.setTranslations(jsonData);
                            dispatch(setI18n(i18n));
                            setLoadingStates((prev) => ({ ...prev, translations: true }));
                        });
                    })
                    .catch(() => {
                        // do nothing
                    });
            };

            fetchTranslations();
        }
    }, [selectedLanguage, setLoaded, userDataLoaded, userSettings.isLoggedIn, settings.apiBaseUrl, dispatch]);

    // refresh access token
    useEffect(() => {
        if (userSettings.accessToken && settings.apiBaseUrl && userSettings.expiresAt > 0) {
            setTimeout(
                () => {
                    requestClientRef.current.get(`/login/refresh`).then((response: any) => {
                        const expiresAt = document.cookie.replace(
                            /(?:(?:^|.*;\s*)expiresAt\s*=\s*([^;]*).*$)|^.*$/,
                            '$1',
                        );

                        if (expiresAt) {
                            dispatch(setUserAccessToken(expiresAt));
                            dispatch(setUserExpiresAt(parseInt(expiresAt)));
                            dispatch(setUserIsLoggedIn(true));
                        } else {
                            dispatch(setUserAccessToken(''));
                            dispatch(setUserExpiresAt(0));
                            dispatch(setUserIsLoggedIn(false));
                        }
                    });
                    // 1 minute before expiration
                },
                userSettings.expiresAt * 1000 - Date.now() - 60000,
            );
        }
    }, [settings.apiBaseUrl, userSettings.accessToken, userSettings.expiresAt, dispatch]);

    useEffect(() => {
        const fetchUserPrompts = async () => {
            try {
                let allUserPrompts: any[] = [];
                let currentPage = 1;
                let totalUserPromptsFetched = 0;
                let totalAvailableUserPrompts = 0;

                do {
                    try {
                        const userPromptsResponse = await requestClientRef.current.get('/prompts/getuserprompts', {
                            params: {
                                page: currentPage,
                            },
                        });

                        if (currentPage === 1) {
                            totalAvailableUserPrompts = userPromptsResponse.data.total;
                            allUserPrompts = userPromptsResponse.data.data;
                        } else {
                            allUserPrompts = allUserPrompts.concat(userPromptsResponse.data.data);
                        }

                        totalUserPromptsFetched += userPromptsResponse.data.data.length;
                        currentPage++;

                        if (!userPromptsResponse.data.data || userPromptsResponse.data.data.length === 0) {
                            break;
                        }
                    } catch (fetchError) {
                        break;
                    }
                } while (totalUserPromptsFetched < totalAvailableUserPrompts);

                if (allUserPrompts !== undefined) {
                    setPrompts(allUserPrompts);
                    setLoadingStates((prev) => ({ ...prev, prompts: true }));
                }
                setLoadingStates((prev) => ({ ...prev, userPrompts: true }));
            } catch (error) {
                setLoadingStates((prev) => ({ ...prev, userPrompts: true }));
            }
        };

        if (userSettings.accessToken && userSettings.roles.length > 0 && settings.apiBaseUrl) {
            fetchUserPrompts();
            dispatch(setReloadPrompts(false));
        }
    }, [
        settings.apiBaseUrl,
        dispatch,
        userSettings.accessToken,
        userSettings.roles,
        userSettings.isSuperAdmin,
        userSettings.isAdmin,
        reloadPrompts,
        setPrompts,
    ]);

    const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)');

    useEffect(() => {
        function handleDarkMode(e: any) {
            let systemTheme = '';
            if (themeSwitcherState === 'system') {
                const darkMode = e.matches;
                const mode = darkMode ? 'dark' : 'light';
                Object.keys(themes).forEach((key: any) => {
                    if (themes[key].system === mode) {
                        systemTheme = key;
                    }
                });

                if (systemTheme === '') {
                    systemTheme = themes[0];
                }
                dispatch(setSelectedTheme(systemTheme));
            }
        }

        prefersDarkMode.addEventListener('change', handleDarkMode);

        return () => {
            prefersDarkMode.removeEventListener('change', handleDarkMode);
        };
    }, [dispatch, prefersDarkMode, themeSwitcherState, themes]);

    // Load theme css
    useEffect(() => {
        const styleElement: HTMLLinkElement = document.querySelector('#themeElement') as HTMLLinkElement;
        const setFaviconHref = (href: string) => {
            const head = document.head;
            const links = Array.from(
                head.querySelectorAll<HTMLLinkElement>(
                    'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]',
                ),
            );
            links.forEach((link) => {
                link.href = href;
            });
        };

        const setAppleWebAppTitle = (title: string) => {
            const meta = document.head.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
            if (meta) {
                meta.content = title;
            }
        };

        const fetchData = async () => {
            setLoadingStates((prev) => ({ ...prev, themes: true }));
            if (!localStorage.getItem('selectedTheme') || localStorage.getItem('selectedTheme') === 'null') {
                localStorage.setItem('selectedTheme', 'system');
            }
            let userTheme = localStorage.getItem('selectedTheme') || 'system';

            let response = await fetch(
                '/themes/' +
                    (settings.themeIdentifier !== '' ? settings.themeIdentifier : projectDomain) +
                    '.json?' +
                    settings.version,
            );

            if (!response.ok) {
                response = await fetch('/themes/default.json?' + settings.version);
            }

            const jsonData = await response.json();
            const themes = jsonData?.themes ?? {};
            dispatch(setAllThemes(themes));
            const titleFromTheme = (jsonData?.title ?? '').toString();
            const brandingTitleOverride = (settings.brandingTitle ?? '').toString().trim();
            const effectiveTitle = brandingTitleOverride !== '' ? brandingTitleOverride : titleFromTheme;
            if (effectiveTitle) {
                document.title = effectiveTitle;
                setAppleWebAppTitle(effectiveTitle);
            }

            const faviconOverride =
                typeof settings.favicon === 'string' && settings.favicon.trim() !== '' ? settings.favicon.trim() : null;
            if (faviconOverride) {
                setFaviconHref(faviconOverride);
            }

            if (Object.keys(themes).indexOf(userTheme) === -1 && userTheme !== 'system') {
                userTheme = jsonData?.defaultTheme;
            }

            if (userTheme !== '' && userTheme !== 'null' && selectedTheme === '') {
                let tmpTheme = userTheme;
                if (themes.length !== 0) {
                    const mode = prefersDarkMode.matches ? 'dark' : 'light';
                    if (userTheme === 'system') {
                        Object.keys(themes).forEach((key: any) => {
                            if (themes[key].system === mode) {
                                tmpTheme = key;
                            }
                        });

                        if (tmpTheme === '') {
                            tmpTheme = themes[0];
                        }
                    }
                }
                dispatch(setSelectedThemeSwitcherState(userTheme));
                dispatch(setSelectedTheme(tmpTheme));
            }

            if (
                selectedTheme === '' ||
                (Object.keys(themes).indexOf(selectedTheme) === -1 && selectedTheme !== 'system')
            ) {
                if (userTheme === '' || userTheme === 'null') {
                    dispatch(setSelectedTheme(jsonData?.defaultTheme));
                }
            }

            if (styleElement) {
                styleElement.onload = () => {
                    setLoadingStates((prev) => ({ ...prev, cssTheme: true }));
                };
            }

            const currentTheme = selectedTheme ?? jsonData?.defaultTheme;

            if (!styleElement) {
                require('./styles/index.css');

                require('./styles/light.css');
                require('./styles/dark.css');
                require('./styles/light.css');
                require('./styles/dark_kdvz.css');
                require('./styles/light_kdvz.css');
                require('./styles/dark_roesler.css');
                require('./styles/light_roesler.css');

                document?.querySelectorAll('style[data-theme]').forEach((style) => {
                    if (!style.textContent?.startsWith('/static')) {
                        style.setAttribute('media', 'none');
                    }
                    if (style.getAttribute('data-theme') === currentTheme) {
                        style.setAttribute('media', 'all');
                    }
                });
                setLoadingStates((prev) => ({ ...prev, cssLocal: true }));
            } else {
                Object.keys(themes).forEach((key: any) => {
                    if (!document.head.querySelector('link[id="' + key + '"]')) {
                        let usedLaterScript = document.createElement('link');
                        usedLaterScript.rel = 'preload';
                        usedLaterScript.as = 'style';
                        usedLaterScript.setAttribute('id', key);
                        usedLaterScript.href = '/themes/css/' + key + '.css?' + settings.version;
                        document.head.appendChild(usedLaterScript);
                    }
                });
            }

            if (currentTheme !== '') {
                if (!themes[currentTheme]) {
                    dispatch(setCurrentThemeColor('light'));
                } else {
                    dispatch(setCurrentThemeColor(themes[currentTheme].themeColor));
                }
                if (themeSwitcherState !== '') localStorage.setItem('selectedTheme', themeSwitcherState);
                styleElement?.setAttribute('href', '/themes/css/' + currentTheme + '.css?' + settings.version);
            }
        };

        if (settings.version !== '0.0.0') {
            fetchData();
        }
    }, [
        settings.version,
        settings.themeIdentifier,
        settings.brandingTitle,
        settings.favicon,
        projectDomain,
        selectedTheme,
        dispatch,
        themeSwitcherState,
        prefersDarkMode.matches,
    ]);

    // fetch selectedTheme and menuState from localStorage
    useEffect(() => {
        const tourStart = localStorage.getItem('tourStart');
        if (tourStart !== 'false') {
            dispatch(setStartTour(true));
        }
    }, [dispatch]);

    // Set App height (e.g. for iOS)
    const appHeight = () => {
        document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };

    useEffect(() => {
        const handleResize = () => {
            appHeight();
        };

        window.addEventListener('resize', handleResize);

        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handlePasswordSubmit = async () => {
        if ((await hash(password ?? '')) === userSettings.userPasswordHash) {
            localStorage.setItem('userPassword', password ?? '');
        }
        document.location.reload();
    };

    const handlePasswordReset = async () => {
        setShowConfirmModal(true);
    };

    const handleConfirmReset = async () => {
        if (isResetting) return;
        setIsResetting(true);

        try {
            const hash: (content: string) => Promise<string> = async (content: string) => {
                const encoder = new TextEncoder();
                const data2 = encoder.encode(content);
                const hash2 = await crypto.subtle.digest('SHA-256', data2);
                return Array.from(new Uint8Array(hash2))
                    .map((b) => b.toString(16).padStart(2, '0'))
                    .join('');
            };

            const data = new URLSearchParams();
            data.append('newUserKey', '');
            data.append('passwordHash', await hash(''));
            await requestClient.post(`/settings/updateusersettings`, data);

            await requestClient.delete(`/chat/deleteall`);

            localStorage.removeItem('userPassword');
            document.location.href = '/';
        } catch (error) {
            setIsResetting(false);
        }
    };

    return loaded &&
        loadingStates.translations &&
        loadingStates.userSettings &&
        loadingStates.generalSettings &&
        loadingStates.prompts &&
        loadingStates.userPrompts &&
        loadingStates.themes &&
        (loadingStates.cssTheme || loadingStates.cssLocal) &&
        loadingStates.password ? (
        <div style={{ height: 'var(--app-height)' }}>
            <App />
        </div>
    ) : (
        <div>
            <img
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: 0,
                    position: 'fixed',
                    left: 0,
                    overflow: 'hidden',
                    top: 0,
                }}
                alt=""
                src="/img/background_varios.jpg"
            />

            {showPasswordModal && !passwordSet && !showConfirmModal && i18n !== null && (
                <div className="flex justify-center items-center fixed z-1 left-0 top-0 w-full h-full overflow-auto bg-black bg-opacity-50">
                    <div className="bg-themeswitch-background m-auto p-4 rounded-lg border border-layout-borderColor max-w-[500px]">
                        <div className="mb-3">
                            <h4>{i18n.translate({ id: 'prompt.password.title', sourceName: 'InitSettings' })}</h4>
                        </div>
                        <div className="mb-3">
                            <input
                                type="password"
                                placeholder={i18n.translate({
                                    id: 'prompt.password.placeholder',
                                    sourceName: 'InitSettings',
                                })}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handlePasswordSubmit();
                                    }
                                }}
                                className="w-full p-3 mb-3 rounded-md bg-input-backgroundColor border border-layout-borderColor focus:outline-none"
                            />
                            <div className="flex gap-2">
                                <Button variant="primary" onClick={handlePasswordSubmit}>
                                    {i18n.translate({ id: 'prompt.password.button', sourceName: 'InitSettings' })}
                                </Button>
                                <Button variant="secondary" onClick={handlePasswordReset}>
                                    {i18n.translate({
                                        id: 'prompt.password.reset',
                                        sourceName: 'InitSettings',
                                    })}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showConfirmModal && i18n !== null && (
                <div className="flex justify-center items-center fixed z-10 left-0 top-0 w-full h-full overflow-auto bg-black bg-opacity-50">
                    <div className="bg-themeswitch-background m-auto p-4 rounded-lg border border-layout-borderColor max-w-[500px]">
                        <div className="mb-4">
                            <h4 className="mb-2">
                                {i18n.translate({
                                    id: 'prompt.password.reset.title',
                                    sourceName: 'InitSettings',
                                })}
                            </h4>
                            <p>
                                {i18n.translate({
                                    id: 'prompt.password.reset.confirm',
                                    sourceName: 'InitSettings',
                                })}
                            </p>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                                {i18n.translate({
                                    id: 'prompt.password.reset.cancel',
                                    sourceName: 'InitSettings',
                                })}
                            </Button>
                            <Button
                                variant="primary"
                                className="!bg-red-600 hover:!bg-red-700"
                                onClick={handleConfirmReset}
                                disabled={isResetting}
                            >
                                {isResetting ? (
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        {i18n.translate({
                                            id: 'prompt.password.reset.confirm.button.loading',
                                            sourceName: 'InitSettings',
                                        })}
                                    </div>
                                ) : (
                                    i18n.translate({
                                        id: 'prompt.password.reset.confirm.button',
                                        sourceName: 'InitSettings',
                                    })
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default InitSettings;
